import {
  AlertType,
  DispositivoType,
  ModelloDispositivoDto,
  ProprietaMisurataTypeGruppo,
  ProprietaMisurataTypeIcona,
  ProprietaMisurataTypeUnitPosition,
  RegistroAlertBindingDto,
  SogliaCheckType,
} from "@/api";
import Button from "@/elements/Button";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { sortBy } from "lodash";
import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

type ProprietaMisurateTableProps = {
  control: Control<ModelloDispositivoDto>;
  watch: UseFormWatch<ModelloDispositivoDto>;
  register: UseFormRegister<ModelloDispositivoDto>;
  errors: FieldErrors<ModelloDispositivoDto>;
  setValue: UseFormSetValue<ModelloDispositivoDto>;
  getValues: UseFormGetValues<ModelloDispositivoDto>;
  disabled?: boolean;
};

const optionalNumber = (value: string) => (value === "" ? null : Number(value));

const requiredNumber = (value: string) => (value === "" ? 0 : Number(value));

const createEmptyRegistroAlertBinding = (
  alertType: AlertType
): RegistroAlertBindingDto => ({
  id: 0,
  alertType: alertType,
  sogliaCheckType: SogliaCheckType.moreThan,
  nome: "",
  registro: "",
});

export default function ProprietaMisurateTable({
  control,
  watch,
  register,
  errors,
  setValue,
  getValues,
  disabled,
}: ProprietaMisurateTableProps) {
  const { t } = useTranslation();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "proprietaMisurataTypes",
    keyName: "fieldId",
  });
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const proprietaMisurateValues =
    useWatch({ control, name: "proprietaMisurataTypes" }) ?? [];

  const getOrderValue = (index: number) => (index + 1) * 100;

  const syncOrders = (indexes?: number[]) => {
    const values = getValues("proprietaMisurataTypes") ?? [];
    const targetIndexes = indexes ?? values.map((_, index) => index);
    targetIndexes.forEach((index) => {
      if (index < 0 || index >= values.length) {
        return;
      }
      const nextOrder = getOrderValue(index);
      if (values[index]?.order !== nextOrder) {
        setValue(`proprietaMisurataTypes.${index}.order`, nextOrder, {
          shouldDirty: true,
        });
      }
    });
  };

  useEffect(() => {
    if (fields.length === 0) {
      setSelectedIndex(null);
      return;
    }
    if (selectedIndex !== null && selectedIndex >= fields.length) {
      setSelectedIndex(fields.length - 1);
    }
  }, [fields.length, selectedIndex]);

  const handleAdd = () => {
    const nextIndex = fields.length;
    append({
      id: 0,
      influxDbTag: "",
      label: "",
      labelEn: "",
      labelDe: "",
      labelWithArticle: "",
      labelWithArticleEn: "",
      labelWithArticleDe: "",
      labelShort: "",
      labelShortEn: "",
      labelShortDe: "",
      unit: "",
      unitEn: "",
      unitDe: "",
      unitPosition: undefined,
      percentMin: null,
      percentMax: null,
      graficoAsseYAutomatico: false,
      decimals: 0,
      order: getOrderValue(nextIndex),
      colorHex: "",
      gruppo: null,
      icona: ProprietaMisurataTypeIcona.Unknown,
      registroAlertBindings: [
        createEmptyRegistroAlertBinding(AlertType.soglia1),
        createEmptyRegistroAlertBinding(AlertType.soglia2),
        createEmptyRegistroAlertBinding(AlertType.soglia3),
        createEmptyRegistroAlertBinding(AlertType.soglia4),
      ],
    });
    setSelectedIndex(nextIndex);
    syncOrders([nextIndex]);
  };

  const handleMove = (from: number, to: number) => {
    if (to < 0 || to >= fields.length) {
      return;
    }
    move(from, to);
    syncOrders([from, to]);
    if (selectedIndex === from) {
      setSelectedIndex(to);
    } else if (selectedIndex === to) {
      setSelectedIndex(from);
    }
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteIndex !== null) {
      const removedIndex = pendingDeleteIndex;
      remove(removedIndex);
      const values = getValues("proprietaMisurataTypes") ?? [];
      syncOrders();
      if (selectedIndex === removedIndex) {
        setSelectedIndex(
          values.length ? Math.min(removedIndex, values.length - 1) : null
        );
      } else if (selectedIndex !== null && selectedIndex > removedIndex) {
        setSelectedIndex(selectedIndex - 1);
      }
    }
    setPendingDeleteIndex(null);
  };

  return (
    <Box mt={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              {t("modelloDispositivoDialog.proprietaMisurate.titolo")}
            </Typography>
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={disabled}
            >
              {t("modelloDispositivoDialog.proprietaMisurate.azioni.aggiungi")}
            </Button>
          </Box>

          {fields.length === 0 ? (
            <Box
              p={3}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ border: "2px dashed divider", borderRadius: 1 }}
            >
              <Typography color="textSecondary" align="center">
                {t(
                  "modelloDispositivoDialog.proprietaMisurate.nessunaProprieta"
                )}
              </Typography>
            </Box>
          ) : (
            <List
              dense
              disablePadding
              sx={{ maxHeight: "600px", overflowY: "auto" }}
            >
              {fields.map((field, index) => {
                const currentValue = proprietaMisurateValues[index];
                const propErrors = errors.proprietaMisurataTypes?.[index];
                const hasErrors = Boolean(
                  propErrors && Object.keys(propErrors).length > 0
                );
                const label = currentValue?.label?.trim()
                  ? currentValue.label
                  : t("modelloDispositivoDialog.proprietaMisurate.nuova");
                return (
                  <ListItem
                    key={field.fieldId}
                    disablePadding
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip
                          title={t(
                            "modelloDispositivoDialog.proprietaMisurate.azioni.spostaSu"
                          )}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMove(index, index - 1)}
                              disabled={disabled || index === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={t(
                            "modelloDispositivoDialog.proprietaMisurate.azioni.spostaGiu"
                          )}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMove(index, index + 1)}
                              disabled={disabled || index === fields.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemButton
                      selected={selectedIndex === index}
                      onClick={() => setSelectedIndex(index)}
                      disabled={disabled}
                      sx={{
                        pr: 7,
                        borderLeft: "3px solid",
                        borderColor: hasErrors ? "error.main" : "transparent",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <Avatar
                          sx={{
                            width: 16,
                            height: 16,
                            bgcolor: currentValue?.colorHex || "grey.300",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {" "}
                        </Avatar>
                      </ListItemIcon>
                      {hasErrors ? (
                        <ListItemIcon
                          sx={{ minWidth: 32, color: "error.main" }}
                        >
                          <Tooltip
                            title={t(
                              "modelloDispositivoDialog.proprietaMisurate.errori"
                            )}
                          >
                            <ErrorOutlineIcon fontSize="small" />
                          </Tooltip>
                        </ListItemIcon>
                      ) : null}
                      <ListItemText
                        primary={label}
                        secondary={
                          currentValue?.influxDbTag || (
                            <i>
                              {t(
                                "modelloDispositivoDialog.proprietaMisurate.topicMqttNonConfigurato"
                              )}
                            </i>
                          )
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Grid>
        <Grid item xs={12} md={8}>
          {fields.length === 0 ? (
            <Box
              p={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                border: "2px solid",
                borderColor: "divider",
                borderRadius: 2,
                height: "600px",
                bgcolor: "background.paper",
              }}
            >
              <Typography color="textSecondary" align="center">
                {t(
                  "modelloDispositivoDialog.proprietaMisurate.nessunaProprieta"
                )}
              </Typography>
            </Box>
          ) : selectedIndex === null ? (
            <Box
              p={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                border: "2px solid",
                borderColor: "divider",
                borderRadius: 2,
                height: "600px",
                bgcolor: "background.paper",
              }}
            >
              <Typography color="textSecondary" align="center">
                {t("modelloDispositivoDialog.proprietaMisurate.seleziona")}
              </Typography>
            </Box>
          ) : (
            <ProprietaMisurataCard
              index={selectedIndex}
              key={selectedIndex}
              control={control}
              watch={watch}
              register={register}
              errors={errors}
              disabled={disabled}
              onDelete={() => setPendingDeleteIndex(selectedIndex)}
            />
          )}
        </Grid>
      </Grid>

      <Dialog
        open={pendingDeleteIndex !== null}
        onClose={() => setPendingDeleteIndex(null)}
      >
        <DialogTitle>
          {t(
            "modelloDispositivoDialog.proprietaMisurate.confermaEliminazione.titolo"
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(
              "modelloDispositivoDialog.proprietaMisurate.confermaEliminazione.messaggio"
            )}
          </Alert>
          <Typography variant="body2">
            {t(
              "modelloDispositivoDialog.proprietaMisurate.confermaEliminazione.messaggio2"
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDelete} color="error" size="small">
            {t("modelloDispositivoDialog.proprietaMisurate.azioni.elimina")}
          </Button>
          <Button
            onClick={() => setPendingDeleteIndex(null)}
            variant="outlined"
            color="dark"
            size="small"
          >
            {t("azioni.annulla")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const gruppoOptions = Object.values(ProprietaMisurataTypeGruppo);
const unitPositionOptions = Object.values(ProprietaMisurataTypeUnitPosition);
const iconaOptions = Object.values(ProprietaMisurataTypeIcona);

type ProprietaMisurataCardProps = {
  index: number;
  control: Control<ModelloDispositivoDto>;
  watch: UseFormWatch<ModelloDispositivoDto>;
  register: UseFormRegister<ModelloDispositivoDto>;
  errors: FieldErrors<ModelloDispositivoDto>;
  disabled?: boolean;
  onDelete: () => void;
};

function ProprietaMisurataCard({
  index,
  control,
  watch,
  register,
  errors,
  disabled,
  onDelete,
}: ProprietaMisurataCardProps) {
  const propErrors = errors.proprietaMisurataTypes?.[index];
  const { t } = useTranslation();
  const tipoDispositivo = useWatch({ control, name: "tipoDispositivo" });
  const showRegistroBindings = tipoDispositivo !== DispositivoType.macchinario;

  return (
    <Card sx={{ border: "2px solid", borderColor: "divider", borderRadius: 2 }}>
      <CardHeader
        title={
          <Typography variant="subtitle2">
            Configura proprietà misurata{" "}
            {useWatch({
              name: `proprietaMisurataTypes.${index}.label`,
              control,
            })}
          </Typography>
        }
        action={
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            disabled={disabled}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        }
        sx={{ py: 1, px: 2, bgcolor: "grey.100" }}
      />
      <CardContent>
        <input
          type="hidden"
          {...register(`proprietaMisurataTypes.${index}.order`, {
            setValueAs: requiredNumber,
          })}
        />

        <Grid container columnSpacing={2} rowSpacing={1}>
          {/* Identificazione e Influx */}
          <Grid item xs={12} md={4}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              {t(
                "modelloDispositivoDialog.proprietaMisurate.sezioni.identificazione"
              )}
            </Typography>
            <Box>
              <InputLabel htmlFor={`proprieta-influxDbTag-${index}`}>
                {t(
                  "modelloDispositivoDialog.proprietaMisurate.colonne.influxDbTag"
                )}
              </InputLabel>
              <TextField
                id={`proprieta-influxDbTag-${index}`}
                size="small"
                fullWidth
                {...register(`proprietaMisurataTypes.${index}.influxDbTag`, {
                  minLength: 1,
                })}
                disabled={disabled}
                error={!!propErrors?.influxDbTag?.message}
              />
              {propErrors?.influxDbTag?.message ? (
                <FormHelperText error>
                  {propErrors.influxDbTag.message}
                </FormHelperText>
              ) : null}
              <InputLabel htmlFor={`proprieta-colorHex-${index}`}>
                {t(
                  "modelloDispositivoDialog.proprietaMisurate.colonne.colorHex"
                )}
              </InputLabel>
              <Controller
                control={control}
                name={`proprietaMisurataTypes.${index}.colorHex`}
                render={({ field: colorField }) => (
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        pointerEvents: disabled ? "none" : "auto",
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      <HexColorPicker
                        color={colorField.value || "#ffffff"}
                        onChange={colorField.onChange}
                        style={{ width: "100%", height: 130 }}
                      />
                    </Box>
                    <TextField
                      id={`proprieta-colorHex-${index}`}
                      size="small"
                      fullWidth
                      placeholder="#FFFFFF"
                      value={colorField.value ?? ""}
                      onChange={(event) =>
                        colorField.onChange(event.target.value)
                      }
                      disabled={disabled}
                      error={!!propErrors?.colorHex?.message}
                    />
                    {propErrors?.colorHex?.message ? (
                      <FormHelperText error>
                        {propErrors.colorHex.message}
                      </FormHelperText>
                    ) : null}
                  </Box>
                )}
              />
            </Box>
          </Grid>

          {/* Etichette (i18n) */}
          <Grid item xs={8}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              {t(
                "modelloDispositivoDialog.proprietaMisurate.sezioni.etichette"
              )}
            </Typography>
            <Grid container columnSpacing={2}>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-label-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.label"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-label-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.label`)}
                  disabled={disabled}
                  error={!!propErrors?.label?.message}
                />
                {propErrors?.label?.message ? (
                  <FormHelperText error>
                    {propErrors.label.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelEn-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelEn"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelEn-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.labelEn`)}
                  disabled={disabled}
                  error={!!propErrors?.labelEn?.message}
                />
                {propErrors?.labelEn?.message ? (
                  <FormHelperText error>
                    {propErrors.labelEn.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelDe-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelDe"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelDe-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.labelDe`)}
                  disabled={disabled}
                  error={!!propErrors?.labelDe?.message}
                />
                {propErrors?.labelDe?.message ? (
                  <FormHelperText error>
                    {propErrors.labelDe.message}
                  </FormHelperText>
                ) : null}
              </Grid>

              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelWithArticle-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelWithArticle"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelWithArticle-${index}`}
                  size="small"
                  fullWidth
                  {...register(
                    `proprietaMisurataTypes.${index}.labelWithArticle`
                  )}
                  disabled={disabled}
                  error={!!propErrors?.labelWithArticle?.message}
                />
                {propErrors?.labelWithArticle?.message ? (
                  <FormHelperText error>
                    {propErrors.labelWithArticle.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelWithArticleEn-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelWithArticleEn"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelWithArticleEn-${index}`}
                  size="small"
                  fullWidth
                  {...register(
                    `proprietaMisurataTypes.${index}.labelWithArticleEn`
                  )}
                  disabled={disabled}
                  error={!!propErrors?.labelWithArticleEn?.message}
                />
                {propErrors?.labelWithArticleEn?.message ? (
                  <FormHelperText error>
                    {propErrors.labelWithArticleEn.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelWithArticleDe-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelWithArticleDe"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelWithArticleDe-${index}`}
                  size="small"
                  fullWidth
                  {...register(
                    `proprietaMisurataTypes.${index}.labelWithArticleDe`
                  )}
                  disabled={disabled}
                  error={!!propErrors?.labelWithArticleDe?.message}
                />
                {propErrors?.labelWithArticleDe?.message ? (
                  <FormHelperText error>
                    {propErrors.labelWithArticleDe.message}
                  </FormHelperText>
                ) : null}
              </Grid>

              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelShort-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelShort"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelShort-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.labelShort`)}
                  disabled={disabled}
                  error={!!propErrors?.labelShort?.message}
                />
                {propErrors?.labelShort?.message ? (
                  <FormHelperText error>
                    {propErrors.labelShort.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelShortEn-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelShortEn"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelShortEn-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.labelShortEn`)}
                  disabled={disabled}
                  error={!!propErrors?.labelShortEn?.message}
                />
                {propErrors?.labelShortEn?.message ? (
                  <FormHelperText error>
                    {propErrors.labelShortEn.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-labelShortDe-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.labelShortDe"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-labelShortDe-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.labelShortDe`)}
                  disabled={disabled}
                  error={!!propErrors?.labelShortDe?.message}
                />
                {propErrors?.labelShortDe?.message ? (
                  <FormHelperText error>
                    {propErrors.labelShortDe.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-unit-${index}`}>
                  {t("modelloDispositivoDialog.proprietaMisurate.colonne.unit")}
                </InputLabel>
                <TextField
                  id={`proprieta-unit-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.unit`)}
                  disabled={disabled}
                  error={!!propErrors?.unit?.message}
                />
                {propErrors?.unit?.message ? (
                  <FormHelperText error>
                    {propErrors.unit.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-unitEn-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.unitEn"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-unitEn-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.unitEn`)}
                  disabled={disabled}
                  error={!!propErrors?.unitEn?.message}
                />
                {propErrors?.unitEn?.message ? (
                  <FormHelperText error>
                    {propErrors.unitEn.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={4}>
                <InputLabel htmlFor={`proprieta-unitDe-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.unitDe"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-unitDe-${index}`}
                  size="small"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.unitDe`)}
                  disabled={disabled}
                  error={!!propErrors?.unitDe?.message}
                />
                {propErrors?.unitDe?.message ? (
                  <FormHelperText error>
                    {propErrors.unitDe.message}
                  </FormHelperText>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          {/* Unità di misura */}
          <Grid item xs={12}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              {t("modelloDispositivoDialog.proprietaMisurate.sezioni.misura")}
            </Typography>
            <Grid container columnSpacing={1}>
              {/* gruppo */}
              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-gruppo-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.gruppo"
                  )}
                </InputLabel>
                <Controller
                  control={control}
                  name={`proprietaMisurataTypes.${index}.gruppo`}
                  render={({ field: gruppoField }) => {
                    return (
                      <TextField
                        select
                        id={`proprieta-gruppo-${index}`}
                        {...gruppoField}
                        value={gruppoField.value ?? ""}
                        onChange={(event) =>
                          gruppoField.onChange(
                            event.target.value === ""
                              ? null
                              : event.target.value
                          )
                        }
                        size="small"
                        fullWidth
                        disabled={disabled}
                        error={!!propErrors?.gruppo?.message}
                      >
                        <MenuItem value="">
                          <em>
                            {t(
                              "modelloDispositivoDialog.proprietaMisurate.azioni.nessuno"
                            )}
                          </em>
                        </MenuItem>
                        {gruppoOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {t(
                              `modelloDispositivoDialog.proprietaMisurate.gruppo.${option}`,
                              { defaultValue: option }
                            )}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Grid>

              {/* icona */}
              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-icona-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.icona"
                  )}
                </InputLabel>
                <Controller
                  control={control}
                  name={`proprietaMisurataTypes.${index}.icona`}
                  render={({ field: iconaField }) => (
                    <>
                      <TextField
                        select
                        id={`proprieta-icona-${index}`}
                        {...iconaField}
                        value={
                          iconaField.value ?? ProprietaMisurataTypeIcona.Unknown
                        }
                        size="small"
                        fullWidth
                        disabled={disabled}
                        error={!!propErrors?.icona?.message}
                      >
                        {iconaOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {t(
                              `modelloDispositivoDialog.proprietaMisurate.icona.${option}`,
                              { defaultValue: option }
                            )}
                          </MenuItem>
                        ))}
                      </TextField>
                      {propErrors?.icona?.message ? (
                        <FormHelperText error>
                          {propErrors.icona.message}
                        </FormHelperText>
                      ) : null}
                    </>
                  )}
                />
              </Grid>

              {/* Posizione */}
              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-unitPosition-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.unitPosition"
                  )}
                </InputLabel>
                <Controller
                  control={control}
                  name={`proprietaMisurataTypes.${index}.unitPosition`}
                  render={({ field: unitPositionField }) => (
                    <>
                      <TextField
                        select
                        id={`proprieta-unitPosition-${index}`}
                        {...unitPositionField}
                        value={unitPositionField.value ?? ""}
                        onChange={(event) =>
                          unitPositionField.onChange(
                            event.target.value === ""
                              ? null
                              : event.target.value
                          )
                        }
                        size="small"
                        fullWidth
                        disabled={disabled}
                        error={!!propErrors?.unitPosition?.message}
                      >
                        <MenuItem value="">
                          <em>
                            {t(
                              "modelloDispositivoDialog.proprietaMisurate.azioni.nessuno"
                            )}
                          </em>
                        </MenuItem>
                        {unitPositionOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {t(
                              `modelloDispositivoDialog.proprietaMisurate.unitPosition.${option}`,
                              { defaultValue: option }
                            )}
                          </MenuItem>
                        ))}
                      </TextField>
                      {propErrors?.unitPosition?.message ? (
                        <FormHelperText error>
                          {propErrors.unitPosition.message}
                        </FormHelperText>
                      ) : null}
                    </>
                  )}
                />
              </Grid>

              {/* Numero di decimali */}
              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-decimals-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.decimals"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-decimals-${index}`}
                  size="small"
                  type="number"
                  fullWidth
                  {...register(`proprietaMisurataTypes.${index}.decimals`, {
                    setValueAs: requiredNumber,
                  })}
                  disabled={disabled}
                  error={!!propErrors?.decimals?.message}
                />
                {propErrors?.decimals?.message ? (
                  <FormHelperText error>
                    {propErrors.decimals.message}
                  </FormHelperText>
                ) : null}
              </Grid>

              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-percentMin-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.percentMin"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-percentMin-${index}`}
                  size="small"
                  type="number"
                  fullWidth
                  inputProps={{ step: "any" }}
                  {...register(`proprietaMisurataTypes.${index}.percentMin`, {
                    setValueAs: optionalNumber,
                  })}
                  disabled={disabled}
                  error={!!propErrors?.percentMin?.message}
                />
                {propErrors?.percentMin?.message ? (
                  <FormHelperText error>
                    {propErrors.percentMin.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={3}>
                <InputLabel htmlFor={`proprieta-percentMax-${index}`}>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.percentMax"
                  )}
                </InputLabel>
                <TextField
                  id={`proprieta-percentMax-${index}`}
                  size="small"
                  type="number"
                  fullWidth
                  inputProps={{ step: "any" }}
                  {...register(`proprietaMisurataTypes.${index}.percentMax`, {
                    setValueAs: optionalNumber,
                  })}
                  disabled={disabled}
                  error={!!propErrors?.percentMax?.message}
                />
                {propErrors?.percentMax?.message ? (
                  <FormHelperText error>
                    {propErrors.percentMax.message}
                  </FormHelperText>
                ) : null}
              </Grid>
              <Grid item xs={6}>
                <InputLabel>
                  {t(
                    "modelloDispositivoDialog.proprietaMisurate.colonne.asseYGrafico"
                  )}
                </InputLabel>
                <Controller
                  control={control}
                  name={`proprietaMisurataTypes.${index}.graficoAsseYAutomatico`}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={disabled}
                        />
                      }
                      label={t(
                        "modelloDispositivoDialog.proprietaMisurate.colonne.graficoAsseYAutomatico"
                      )}
                      sx={{ ml: 1 }}
                    />
                  )}
                />
              </Grid>

              {showRegistroBindings && (
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={12}>
                      <RegistroAlertBindingsTable
                        key={index}
                        control={control}
                        watch={watch}
                        register={register}
                        errors={errors}
                        index={index}
                        disabled={disabled}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

type RegistroAlertBindingsTableProps = {
  control: Control<ModelloDispositivoDto>;
  watch: UseFormWatch<ModelloDispositivoDto>;
  register: UseFormRegister<ModelloDispositivoDto>;
  errors?: FieldErrors<ModelloDispositivoDto>;
  index: number;
  disabled?: boolean;
};

function RegistroAlertBindingsTable({
  control,
  watch,
  register,
  errors,
  index,
  disabled,
}: RegistroAlertBindingsTableProps) {
  const { t } = useTranslation();
  const bindingsKey =
    `proprietaMisurataTypes.${index}.registroAlertBindings` as const;
  const { fields, append } = useFieldArray({
    control,
    name: bindingsKey,
    keyName: "fieldId",
  });

  useEffect(() => {
    const requiredTypes = [
      AlertType.soglia1,
      AlertType.soglia2,
      AlertType.soglia3,
      AlertType.soglia4,
    ];

    const bindings = watch(bindingsKey);
    requiredTypes.forEach((type) => {
      if (bindings && !bindings.some((f) => f.alertType === type)) {
        append(createEmptyRegistroAlertBinding(type));
      }
    });
  }, [fields, append, bindingsKey]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        mb={1}
      >
        <Typography variant="overline" color="primary" fontWeight="bold">
          {t("modelloDispositivoDialog.proprietaMisurate.bindings.titolo")}
        </Typography>
      </Box>
      <Table size="small">
        <Box component="thead">
          <TableRow>
            <TableCell>
              {t(
                "modelloDispositivoDialog.proprietaMisurate.bindings.colonne.alertType"
              )}
            </TableCell>
            <TableCell>
              {t(
                "modelloDispositivoDialog.proprietaMisurate.bindings.colonne.nome"
              )}
            </TableCell>
            <TableCell>
              {t(
                "modelloDispositivoDialog.proprietaMisurate.bindings.colonne.registro"
              )}
            </TableCell>
          </TableRow>
        </Box>
        <TableBody>
          {sortBy(fields, (x) => x.alertType ?? x.id).map((field) => {
            const bindingIndex = fields.findIndex(
              (f) => f.fieldId === field.fieldId
            );
            const bindingErrors = (errors?.proprietaMisurataTypes as any)?.[
              index
            ]?.registroAlertBindings?.[bindingIndex];
            return (
              <TableRow key={field.fieldId}>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(
                      `modelloDispositivoDialog.proprietaMisurate.alertType.${field.alertType}`,
                      { defaultValue: field.alertType }
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    {...register(
                      `proprietaMisurataTypes.${index}.registroAlertBindings.${bindingIndex}.nome`
                    )}
                    disabled={disabled}
                    error={!!bindingErrors?.nome?.message}
                  />
                  {bindingErrors?.nome?.message ? (
                    <FormHelperText error>
                      {bindingErrors.nome.message}
                    </FormHelperText>
                  ) : null}
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    {...register(
                      `proprietaMisurataTypes.${index}.registroAlertBindings.${bindingIndex}.registro`
                    )}
                    disabled={disabled}
                    error={!!bindingErrors?.registro?.message}
                  />
                  {bindingErrors?.registro?.message ? (
                    <FormHelperText error>
                      {bindingErrors.registro.message}
                    </FormHelperText>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
